/**
 * Lightweight localStorage-backed wizard draft for the tournament creation flow.
 * Lets the commissioner close the tab and resume later. Per-step state is
 * tiny so we keep it as a single JSON blob keyed by a stable id.
 */

const KEY = 'sktourney.tournamentDraft.v1';

import type {
  CompetitionFormat,
  LeagueConfig,
  PlayoffFormat,
  RegistrationIntakeType,
  ScoringSettings,
  Weekday,
} from '../models';

export type ModuleKey = 'locker' | 'stands' | 'frontDesk' | 'proShop' | 'concessions' | 'gamification';

export interface TournamentDraft {
  id: string;
  step: number;
  format: CompetitionFormat;
  typeId: string;
  name: string;
  tagline?: string;
  startsAt: string;
  endsAt: string;
  venueName: string;
  venueAddress: string;
  venuePostalCode?: string;
  venueCity?: string;
  venueRegion?: string;
  rinkCount: number;
  rosterChoice: 'upload' | 'manual' | 'registration' | 'later' | '';
  seasonWeekdays: Weekday[];
  seasonFirstPuckTime: string;
  seasonGamesPerRinkPerNight: number;
  seasonRegularSeasonEndsAt: string;
  seasonBlackoutDates: string[];
  registrationIntakeType: RegistrationIntakeType;
  registrationOpensAt: string;
  registrationClosesAt: string;
  registrationFeeCents: number;
  registrationRequireWaiver: boolean;
  registrationAutoApprove: boolean;
  scoring: ScoringSettings;
  playoffsEnabled: boolean;
  playoffFormat: PlayoffFormat;
  playoffQualifyingTeams: number;
  playoffSeedingSource: 'final_standings' | 'manual';
  modules: Record<ModuleKey, boolean>;
  updatedAt: string;
}

export function emptyDraft(format: CompetitionFormat = 'tournament', league?: LeagueConfig): TournamentDraft {
  const isSeason = format === 'season';
  const defaults = league?.eventDefaults;
  const seasonDefaults = defaults?.season;
  const seasonTypeId = defaults?.seasonTypeId === 'ironman-season'
    ? 'roller-season'
    : defaults?.seasonTypeId ?? 'roller-season';
  const draft: TournamentDraft = {
    id: `draft-${Date.now().toString(36)}`,
    step: 0,
    format,
    typeId: isSeason ? seasonTypeId : defaults?.tournamentTypeId ?? 'ironman',
    name: '',
    tagline: '',
    startsAt: '',
    endsAt: '',
    venueName: '',
    venueAddress: '',
    venuePostalCode: '',
    venueCity: '',
    venueRegion: '',
    rinkCount: defaults?.rinkCount ?? 1,
    rosterChoice: isSeason ? seasonDefaults?.rosterIntake ?? 'later' : '',
    seasonWeekdays: isSeason ? seasonDefaults?.weekdays ?? [] : [],
    seasonFirstPuckTime: isSeason ? seasonDefaults?.firstPuckTime ?? '19:00' : '',
    seasonGamesPerRinkPerNight: isSeason ? seasonDefaults?.gamesPerRinkPerNight ?? 3 : 1,
    seasonRegularSeasonEndsAt: '',
    seasonBlackoutDates: [],
    registrationIntakeType: seasonDefaults?.registrationIntakeType ?? 'individual',
    registrationOpensAt: '',
    registrationClosesAt: '',
    registrationFeeCents: seasonDefaults?.registrationFeeCents ?? 5000,
    registrationRequireWaiver: seasonDefaults?.registrationRequireWaiver ?? true,
    registrationAutoApprove: seasonDefaults?.registrationAutoApprove ?? false,
    scoring: isSeason
      ? {
          periodCount: seasonDefaults?.periodCount ?? 3,
          periodLengthMinutes: seasonDefaults?.periodLengthMinutes ?? 12,
          trackAssists: true,
          trackPenaltyMinutes: true,
          trackPenaltyShotsOnly: false,
          trackGoalTimestamps: true,
          gameLengthMinutes: (seasonDefaults?.periodCount ?? 3) * (seasonDefaults?.periodLengthMinutes ?? 12),
          overtimeFormat: seasonDefaults?.overtimeFormat ?? 'straight_shootout',
          overtimeMinutes: seasonDefaults?.overtimeMinutes ?? 5,
          overtimeSkaters: seasonDefaults?.overtimeSkaters ?? 3,
          shootoutRoundCount: seasonDefaults?.shootoutRoundCount ?? 3,
          pointsRegulationWin: seasonDefaults?.pointsRegulationWin ?? 2,
          pointsOvertimeWin: seasonDefaults?.pointsOvertimeWin ?? 2,
          pointsShootoutWin: seasonDefaults?.pointsShootoutWin ?? 2,
          pointsOvertimeLoss: seasonDefaults?.pointsOvertimeLoss ?? 1,
          pointsShootoutLoss: seasonDefaults?.pointsShootoutLoss ?? 1,
          pointsTie: seasonDefaults?.pointsTie ?? 1,
          tiebreakers: seasonDefaults?.tiebreakers?.length
            ? seasonDefaults.tiebreakers
            : ['wins', 'goal_diff', 'goals_for', 'head_to_head', 'fewest_pims'],
        }
      : {
          trackAssists: true,
          trackPenaltyMinutes: false,
          trackPenaltyShotsOnly: true,
          trackGoalTimestamps: false,
          gameLengthMinutes: 12,
          overtimeFormat: 'straight_shootout',
          shootoutRoundCount: 3,
          pointsRegulationWin: 2,
          pointsOvertimeWin: 2,
          pointsShootoutWin: 2,
          pointsOvertimeLoss: 1,
          pointsShootoutLoss: 1,
          pointsTie: 1,
          tiebreakers: ['wins', 'goal_diff', 'goals_for', 'head_to_head'],
        },
    playoffsEnabled: isSeason ? seasonDefaults?.playoffsEnabled ?? true : false,
    playoffFormat: seasonDefaults?.playoffFormat ?? 'single_elim',
    playoffQualifyingTeams: seasonDefaults?.playoffQualifyingTeams ?? 4,
    playoffSeedingSource: 'final_standings',
    modules: {
      locker: true,
      stands: true,
      frontDesk: true,
      proShop: false,
      concessions: false,
      gamification: false,
      ...(defaults?.modules ?? {}),
    },
    updatedAt: new Date().toISOString(),
  };

  if (league?.homeRink) {
    const homeRink = league.homeRink;
    draft.venueName = homeRink.name ?? '';
    draft.venueAddress = homeRink.address ?? '';
    draft.venuePostalCode = homeRink.postalCode ?? '';
    draft.venueCity = homeRink.city ?? '';
    draft.venueRegion = homeRink.region ?? '';
  }

  return draft;
}

export function loadDraft(): TournamentDraft | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? normalizeDraft(JSON.parse(raw) as Partial<TournamentDraft>) : null;
  } catch {
    return null;
  }
}

export function saveDraft(draft: TournamentDraft): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      KEY,
      JSON.stringify({ ...draft, updatedAt: new Date().toISOString() }),
    );
  } catch {
    /* ignore */
  }
}

export function clearDraft(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(KEY);
}

function normalizeDraft(input: Partial<TournamentDraft>): TournamentDraft {
  const base = emptyDraft(input.format ?? 'tournament');
  const typeId =
    input.format === 'season' && input.typeId === 'ironman-season'
      ? 'roller-season'
      : input.format === 'season' && input.typeId && !input.typeId.includes('season')
      ? 'roller-season'
      : input.typeId ?? base.typeId;
  return {
    ...base,
    ...input,
    typeId,
    modules: { ...base.modules, ...(input.modules ?? {}) },
    scoring: { ...base.scoring, ...(input.scoring ?? {}) },
    seasonWeekdays: input.seasonWeekdays?.length ? input.seasonWeekdays : base.seasonWeekdays,
    seasonBlackoutDates: input.seasonBlackoutDates ?? base.seasonBlackoutDates,
  };
}
